import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Boxes, Cable, Clock, Megaphone, ShoppingBag, Sparkles, X } from 'lucide-react';

import { HealthBanner } from '@/components/layout/HealthBanner';
import { ExpiredTopBanner } from '@/components/layout/ExpiredTopBanner';
import { Badge } from '@/components/ui/badge';
import { ProPill } from '@/components/ui/pro-pill';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CircularProgress } from '@/components/ui/circular-progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { KPICard } from '@/components/ui/kpi-card';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ProgressBar } from '@/components/ui/progress-bar';
import { SectionHeader } from '@/components/ui/section-header';
import { Select } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { SoftCard } from '@/components/ui/soft-card';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTier } from '@/lib/tier';
import { cn } from '@/lib/utils';

/**
 * Component preview surface — outside the AppLayout so it can be opened
 * side-by-side with the Lovable Lovable.dev preview without the dashboard
 * sidebar getting in the way. Dev only.
 */
function Section({ title, description, children }) {
	return (
		<section className="space-y-3">
			<div>
				<h2 className="text-lg font-semibold text-foreground">{title}</h2>
				{description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
			</div>
			<SoftCard className="space-y-4">{children}</SoftCard>
		</section>
	);
}

export function DesignGuidelines() {
	const { tier, setTier } = useTier();
	return (
		<div className="min-h-screen bg-background px-6 py-10 md:px-12">
			<div className="mx-auto max-w-5xl space-y-10">
				<header className="space-y-2">
					<div className="text-xs font-semibold uppercase tracking-[0.08em] text-primary-soft-foreground">Unify by NearSt</div>
					<h1 className="text-4xl font-semibold">Design guidelines</h1>
					<p className="text-sm text-muted-foreground">
						Open this page side-by-side with the Lovable prototype to compare components.
					</p>
				</header>

				<Section
					title="Plan tier"
					description="No backend source for the plan tier yet — this dev switch drives the Channels page Pro / Starter behaviour."
				>
					<div className="flex items-center gap-3">
						<div className="inline-flex items-center gap-1 rounded-full border border-border bg-white p-1">
							{['starter', 'pro'].map((value) => (
								<button
									key={value}
									type="button"
									onClick={() => setTier(value)}
									className={cn(
										'rounded-full px-3 py-1 text-xs font-semibold capitalize transition',
										tier === value ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
									)}
								>
									{value}
								</button>
							))}
						</div>
						{tier === 'pro' ? <ProPill /> : null}
					</div>
				</Section>

				<section className="space-y-3" id="health-banners">
					<div>
						<h2 className="text-lg font-semibold text-foreground">Health banners (NEA-5713)</h2>
						<p className="text-sm text-muted-foreground">
							Renders edge-to-edge inside `AppLayout` between the LifecycleBanner and the page content. Severity vocabulary
							mirrors the LifecycleBanner soft / hard idiom. Visual final word goes to Max.
						</p>
					</div>
					<div className="overflow-hidden rounded-2xl border border-border bg-white">
						<HealthBanner
							id="dg-pos-disconnected"
							severity="urgent"
							icon={Cable}
							title="Your POS isn't connected"
							body="Stock changes from your till aren't reaching any of your channels."
							ctaLabel="Reconnect POS"
							ctaTo="/onboarding?step=3"
						/>
						<HealthBanner
							id="dg-gbp-revoked"
							severity="urgent"
							icon={AlertTriangle}
							title="Your Google Business Profile is disconnected"
							body="Reconnect to keep Google Local Listings and Local Ads running."
							ctaLabel="Reconnect Google"
							ctaTo="/onboarding?step=2"
						/>
						<HealthBanner
							id="dg-all-stock-rejected"
							severity="urgent"
							icon={ShoppingBag}
							title="None of your products are visible to shoppers"
							body="Every item in your catalogue is being rejected — fix the product errors to start showing again."
							ctaLabel="Fix product errors"
							ctaTo="/inventory"
						/>
						<HealthBanner
							id="dg-stale-soft"
							severity="soft"
							icon={Clock}
							title="Your stock hasn't synced in over a week"
							body="Last sync 8 days ago. Every channel is showing what your shop had then."
							ctaLabel="Check connection"
							ctaTo="/inventory/connection"
						/>
						<HealthBanner
							id="dg-stale-warning"
							severity="warning"
							icon={Clock}
							title="Your stock is two weeks out of date"
							body="Last sync 17 days ago. Every channel is showing what your shop had then."
							ctaLabel="Check connection"
							ctaTo="/inventory/connection"
						/>
						<HealthBanner
							id="dg-stale-error"
							severity="error"
							icon={Clock}
							title="Your stock has been frozen for over a month"
							body="Last sync 34 days ago. Every channel is showing what your shop had then."
							ctaLabel="Check connection"
							ctaTo="/inventory/connection"
						/>
					</div>
				</section>

				<section className="space-y-3" id="trial-banners">
					<div>
						<h2 className="text-lg font-semibold text-foreground">Trial lifecycle banners (existing)</h2>
						<p className="text-sm text-muted-foreground">
							Driven by `data/banners.json` and `useTrialStatus()`. Two condition-gated variants — a soft day-10+ nudge and a
							hard near-expiry banner — plus the post-expiry full-bleed top banner.
						</p>
					</div>
					<div className="overflow-hidden rounded-2xl border border-border bg-white">
						{/* trial-soft: info variant, dismissible, primary text-link CTA, day 10+ */}
						<div className="w-full border-b border-primary/10 bg-info-soft text-info-soft-foreground">
							<div className="mx-auto flex max-w-[1100px] items-center gap-3 px-6 py-2.5 md:px-10">
								<Sparkles className="h-4 w-4 shrink-0 text-primary" />
								<span className="text-sm font-medium">
									You're on day 12 of 30. Things are looking good. Continue for £1/month and keep the momentum. Cancel any
									time.
								</span>
								<div className="ml-auto flex items-center gap-2">
									<Link
										to="/settings/billing"
										className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
									>
										Add payment <ArrowRight className="h-3.5 w-3.5" />
									</Link>
									<button
										type="button"
										aria-label="Dismiss banner"
										className="rounded-md p-1 text-muted-foreground transition hover:text-foreground"
									>
										<X className="h-4 w-4" />
									</button>
								</div>
							</div>
						</div>
						{/* trial-hard: warning variant with dark CTA, non-dismissible */}
						<div className="w-full border-b border-action/20 bg-action-soft text-action-soft-foreground">
							<div className="mx-auto flex max-w-[1100px] items-center gap-3 px-6 py-2.5 md:px-10">
								<span className="text-sm font-medium">Your store pauses in 3 days. Continue for £1/month.</span>
								<div className="ml-auto flex items-center gap-2">
									<button
										type="button"
										className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition hover:opacity-90"
									>
										Add payment <ArrowRight className="h-3.5 w-3.5" />
									</button>
								</div>
							</div>
						</div>
					</div>

					<div data-preview-id="expired-top">
						<ExpiredTopBanner />
					</div>
				</section>

				<section className="space-y-3" id="pro-banner">
					<div>
						<h2 className="text-lg font-semibold text-foreground">Pro allowance banner (Channels page)</h2>
						<p className="text-sm text-muted-foreground">
							Page-level affordance shown on the Channels page when the shop is on the Pro plan. Communicates the
							2-paid-channel allowance + remaining slots.
						</p>
					</div>
					<div className="space-y-3" data-preview-id="pro-allowance">
						{[
							{ id: 'pro-empty', slotsUsed: 0, slotsLimit: 2 },
							{ id: 'pro-one', slotsUsed: 1, slotsLimit: 2 },
							{ id: 'pro-full', slotsUsed: 2, slotsLimit: 2 }
						].map(({ id, slotsUsed, slotsLimit }) => {
							const detail =
								slotsUsed === 0
									? "Pick any 2 paid channels — they're on us. Additional channels are charged as listed."
									: slotsUsed >= slotsLimit
										? "You're using both included slots. Additional channels are charged as listed."
										: `Using ${slotsUsed} of ${slotsLimit} included slots. One more on us.`;
							return (
								<div
									key={id}
									data-banner-id={id}
									className="flex flex-wrap items-center gap-4 rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 via-card to-action/5 p-4 md:p-5"
								>
									<ProPill className="shrink-0 px-2.5 py-1 text-xs" />
									<div className="min-w-0 flex-1">
										<div className="text-sm font-bold text-foreground">Pro includes 2 paid channels free</div>
										<div className="mt-0.5 text-xs text-muted-foreground">{detail}</div>
									</div>
									<div className="flex items-center gap-1.5">
										{Array.from({ length: slotsLimit }).map((_, index) => (
											<span
												key={index}
												className={cn('h-2 w-8 rounded-full', index < slotsUsed ? 'bg-primary' : 'bg-primary/20')}
											/>
										))}
										<span className="ml-2 text-xs font-semibold tabular-nums text-foreground">
											{slotsUsed}/{slotsLimit}
										</span>
									</div>
								</div>
							);
						})}
					</div>
				</section>

				<section className="space-y-3" id="ads-paused-hero">
					<div>
						<h2 className="text-lg font-semibold text-foreground">Ads paused hero (Ads page)</h2>
						<p className="text-sm text-muted-foreground">
							Page-level in-content hero shown at the top of the Ads page when the shop's most recent ad-spend invoice failed.
							Not a layout banner — it sits inside the page so the rest of the dashboard chrome stays normal.
						</p>
					</div>
					<div data-preview-id="ads-paused">
						<SoftCard className="flex flex-wrap items-start gap-4 border border-destructive/30 bg-destructive/5">
							<AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-destructive" />
							<div className="min-w-0 flex-1">
								<h3 className="text-base font-bold text-destructive">Your ads are paused because a payment failed</h3>
								<p className="mt-1 text-sm text-muted-foreground">
									We couldn't charge your card for recent ad spend, so we've paused your Google Ads campaigns. Update your
									payment method to resume them — we'll restart automatically once the outstanding invoice clears.
								</p>
							</div>
							<Link
								to="/settings/billing"
								className="inline-flex items-center gap-1.5 rounded-full bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground transition hover:opacity-90"
							>
								Update payment <ArrowRight className="h-4 w-4" />
							</Link>
						</SoftCard>
					</div>
				</section>

				<Section title="Buttons" description="Primary action / brand action / secondary affordances.">
					<div className="flex flex-wrap items-center gap-3">
						<Button>Default</Button>
						<Button variant="action">Action</Button>
						<Button variant="secondary">Secondary</Button>
						<Button variant="outline">Outline</Button>
						<Button variant="ghost">Ghost</Button>
						<Button variant="link">Link</Button>
						<Button variant="destructive">Destructive</Button>
						<Button size="sm">Small</Button>
						<Button size="lg">Large</Button>
						<Button disabled>Disabled</Button>
					</div>
				</Section>

				<Section title="Badges">
					<div className="flex flex-wrap items-center gap-2">
						<Badge>Default</Badge>
						<Badge variant="secondary">Secondary</Badge>
						<Badge variant="success">Success</Badge>
						<Badge variant="warning">Warning</Badge>
						<Badge variant="info">Info</Badge>
						<Badge variant="action">Action</Badge>
						<Badge variant="destructive">Destructive</Badge>
						<Badge variant="outline">Outline</Badge>
					</div>
				</Section>

				<Section title="Status indicator">
					<div className="flex flex-wrap items-center gap-6">
						<StatusIndicator tone="live" label="Live" />
						<StatusIndicator tone="warning" label="In progress" />
						<StatusIndicator tone="error" label="Error" />
						<StatusIndicator tone="info" label="Info" />
						<StatusIndicator tone="muted" label="Idle" />
					</div>
				</Section>

				<Section title="Soft cards">
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
						<SoftCard tone="white">White</SoftCard>
						<SoftCard tone="muted">Muted</SoftCard>
						<SoftCard tone="accent">Accent</SoftCard>
						<SoftCard tone="soft">Soft gradient</SoftCard>
					</div>
				</Section>

				<Section title="KPIs">
					<div className="grid gap-3 sm:grid-cols-3">
						<KPICard label="Views" value={6065} trend={33.7} helpText="last 30 days" />
						<KPICard label="Engagements" value={1199} trend={60.1} helpText="last 30 days" />
						<KPICard label="Orders" value={24} trend={-4.2} helpText="last 30 days" />
					</div>
				</Section>

				<Section title="Section header">
					<SectionHeader
						eyebrow="Local Potential"
						title="How well are you using your local advantage?"
						sub="A composite score."
						rightSlot={<Button size="sm">View</Button>}
					/>
				</Section>

				<Section title="Progress">
					<ProgressBar value={86} tone="success" label="Active products" showValue />
					<ProgressBar value={52} tone="primary" label="Onboarding" showValue />
					<ProgressBar value={23} tone="warning" label="Need attention" showValue />
					<div className="pt-2">
						<CircularProgress value={76} max={100} label={76} sublabel="of 100" />
					</div>
				</Section>

				<Section title="Form controls">
					<div className="grid gap-4 md:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="dg-input">Input</Label>
							<Input id="dg-input" placeholder="Type here…" />
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="dg-select">Select</Label>
							<Select id="dg-select" defaultValue="gbp">
								<option value="gbp">GBP</option>
								<option value="eur">EUR</option>
								<option value="usd">USD</option>
							</Select>
						</div>
						<div className="space-y-1.5 md:col-span-2">
							<Label htmlFor="dg-textarea">Textarea</Label>
							<Textarea id="dg-textarea" placeholder="Notes…" rows={3} />
						</div>
						<Toggle defaultChecked label="Enable channel" />
					</div>
				</Section>

				<Section title="Tabs">
					<Tabs defaultValue="overview">
						<TabsList>
							<TabsTrigger value="overview">Overview</TabsTrigger>
							<TabsTrigger value="orders">Orders</TabsTrigger>
							<TabsTrigger value="settings">Settings</TabsTrigger>
						</TabsList>
						<TabsContent value="overview">Overview content</TabsContent>
						<TabsContent value="orders">Orders content</TabsContent>
						<TabsContent value="settings">Settings content</TabsContent>
					</Tabs>
				</Section>

				<Section title="Table">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Product</TableHead>
								<TableHead className="text-right">Impressions</TableHead>
								<TableHead className="text-right">Clicks</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<TableRow>
								<TableCell>NeeDoh Nice Ice Baby</TableCell>
								<TableCell className="text-right tabular-nums">450</TableCell>
								<TableCell className="text-right tabular-nums">10</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Pokemon TCG: Mega Evolution Tin</TableCell>
								<TableCell className="text-right tabular-nums">237</TableCell>
								<TableCell className="text-right tabular-nums">19</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</Section>

				<Section title="Dialog / Sheet / Popover / Tooltip">
					<TooltipProvider>
						<div className="flex flex-wrap gap-3">
							<Dialog>
								<DialogTrigger asChild>
									<Button variant="outline">Open dialog</Button>
								</DialogTrigger>
								<DialogContent>
									<DialogHeader>
										<DialogTitle>Are you sure?</DialogTitle>
										<DialogDescription>This action can't be undone.</DialogDescription>
									</DialogHeader>
								</DialogContent>
							</Dialog>
							<Sheet>
								<SheetTrigger asChild>
									<Button variant="outline">Open sheet</Button>
								</SheetTrigger>
								<SheetContent side="right">
									<SheetHeader>
										<SheetTitle>Side sheet</SheetTitle>
										<SheetDescription>Useful for filters and detail panels.</SheetDescription>
									</SheetHeader>
								</SheetContent>
							</Sheet>
							<Popover>
								<PopoverTrigger asChild>
									<Button variant="outline">Popover</Button>
								</PopoverTrigger>
								<PopoverContent>Anchored content goes here.</PopoverContent>
							</Popover>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button variant="outline">Hover me</Button>
								</TooltipTrigger>
								<TooltipContent>I'm a tooltip</TooltipContent>
							</Tooltip>
						</div>
					</TooltipProvider>
				</Section>

				<Section title="Card (shadcn-style)">
					<Card>
						<CardHeader>
							<CardTitle>Card title</CardTitle>
						</CardHeader>
						<CardContent>The thinner shadcn card. Prefer SoftCard for the Unify look; this is for chart canvases.</CardContent>
					</Card>
				</Section>

				<Section title="Empty state">
					<EmptyState
						icon={ShoppingBag}
						title="No orders yet"
						body="Once shoppers place reservations, you'll see them here."
						primaryCta="View channels"
						primaryHref="/channels"
					/>
				</Section>

				<Section title="Brand gradients">
					<div className="rounded-2xl bg-gradient-unify p-8 text-center text-white">bg-gradient-unify</div>
					<div className="rounded-2xl bg-gradient-soft p-8 text-center">bg-gradient-soft</div>
					<div className="text-center text-3xl font-semibold text-gradient-unify">text-gradient-unify</div>
				</Section>

				<Section title="Icons (lucide)">
					<div className="flex flex-wrap items-center gap-3 text-foreground">
						<ShoppingBag className="h-5 w-5" />
						<Boxes className="h-5 w-5" />
						<Megaphone className="h-5 w-5" />
					</div>
				</Section>
			</div>
		</div>
	);
}
